import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getAvailableRoles } from '../utils/roleUtils';
import { getDefaultPermissions, EMPLOYEE_MODULES, isCrewSeatRole, FULL_TIER_ONLY_MODULES } from '../utils/employeePermissions';
import PhoneField from './Common/PhoneField';
import { isPlausiblePhone, phoneErrorMessage } from '../utils/phone';
import { PAY_TYPES, STANDARD_ANNUAL_HOURS, hourlyCostFromSalary, roundRate } from '../utils/employeeCompensation';

import GlassModal from './Common/GlassModal';

const EmployeeInviteModal = ({ isOpen, onClose, onInvite }) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    role: 'technician',
    pay_type: 'hourly',
    hourly_rate: '',
    overtime_rate: '',
    annual_salary: '',
    department: '',
    capacity_hours_per_day: '8',
    job_title: ''
  });

  // ✅ NEW: Use unified module structure with role-based defaults
  const [permissions, setPermissions] = useState(() =>
    getDefaultPermissions('technician')
  );

  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [submitError, setSubmitError] = useState(''); // #939

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setFormData(prev => ({ ...prev, role: newRole }));
    // ✅ Auto-update permissions based on role
    setPermissions(getDefaultPermissions(newRole));
  };

  // Same seat cap as the Add/Edit form (Employees.js isSeatLockedModule). A Crew seat can never
  // hold a Full-tier module and the runtime strips it on every read, so an invite that saved it
  // on showed access that never arrived (board #4422).
  const isSeatLocked = (moduleKey) => isCrewSeatRole(formData.role) && FULL_TIER_ONLY_MODULES.includes(moduleKey);

  const handlePermissionToggle = (moduleKey) => {
    if (isSeatLocked(moduleKey)) return;
    setPermissions(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Board #463. The phone went to onInvite as whatever was typed. Phone is optional here, so
    // only a NON-EMPTY value that is not a usable number is refused — blank still invites.
    const phone = String(formData.phone || '').trim();
    if (phone && !isPlausiblePhone(phone)) {
      setPhoneError(phoneErrorMessage(phone));
      return;
    }
    setPhoneError('');

    setLoading(true);
    setSubmitError('');
    try {
      // ✅ Pass form data to parent (permissions will be saved to employee_permissions table)
      const cappedPermissions = { ...permissions };
      FULL_TIER_ONLY_MODULES.forEach((key) => {
        if (isCrewSeatRole(formData.role) && key in cappedPermissions) cappedPermissions[key] = false;
      });
      await onInvite({
        ...formData,
        permissions: cappedPermissions
      });

      // Reset form
      setFormData({
        email: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        phone: '',
        role: 'technician',
        pay_type: 'hourly',
        hourly_rate: '',
        overtime_rate: '',
        annual_salary: '',
        department: '',
        capacity_hours_per_day: '8',
        job_title: ''
      });
      setPermissions(getDefaultPermissions('technician'));
      onClose();
    } catch (error) {
      console.error('Invite error:', error);
      // #939: keep the dialog and what was typed; say why it failed right here.
      setSubmitError(error?.message || 'The invite could not be sent.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-5xl"
      closeOnOverlay={false}
      header={(
        <>
          <div>
            <h3 className="text-xl font-semibold">Invite Employee</h3>
            <p className="text-primary-100/80 text-sm">Send email invite with module access</p>
          </div>
          <button type="button" aria-label="Close" title="Close"
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
        {/* Main Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto space-y-8">

          {/* Top Half - Contact Information */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
            <h4 className="text-lg font-semibold text-gray-200 mb-6 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="employee@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="John"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Middle Name
                </label>
                <input
                  type="text"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Robert (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Smith"
                />
              </div>

              <div>
                {/* Board #463: a raw type=tel box with no country and no validation, so letters
                    and ambiguous local numbers reached the invite. Shared control, and the
                    heavier Employees styling is passed in rather than forked. */}
                <PhoneField
                  label="Phone Number"
                  name="phone"
                  testId="invite-input-phone"
                  value={formData.phone}
                  onChange={(phone) => setFormData(prev => ({ ...prev, phone }))}
                  error={phoneError}
                  labelClassName="block text-sm font-medium text-gray-300 mb-2"
                  inputClassName="flex-1 min-w-0 px-4 py-3 border border-l-0 border-white/20 dark:border-slate-700/40 rounded-r-lg bg-white/70 dark:bg-slate-800/60 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role *
                </label>
                <select
                  required
                  value={formData.role}
                  onChange={handleRoleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {getAvailableRoles().map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Permissions will be automatically set based on role (you can customize them below)
                </p>
              </div>
            </div>
          </div>

          {/* Middle Half - Compensation & Employment Details */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
            <h4 className="text-lg font-semibold text-gray-200 mb-6 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Compensation & Employment
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pay Type
                </label>
                {/* Board #452: the invite form carried its own copy of the pay-type list,
                    piece rate included. Same list as the Employees page now, from one owner. */}
                <select
                  name="pay_type"
                  value={formData.pay_type || 'hourly'}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {PAY_TYPES.map((pt) => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Used to cost jobs. Not payroll.</p>
              </div>

              {(formData.pay_type || 'hourly') === 'salary' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Annual Salary ($)
                  </label>
                  <input
                    type="number"
                    name="annual_salary"
                    min="0"
                    step="0.01"
                    value={formData.annual_salary || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {hourlyCostFromSalary(formData.annual_salary) > 0
                      ? `Costs jobs at $${roundRate(hourlyCostFromSalary(formData.annual_salary)).toFixed(2)}/hr (salary ÷ ${STANDARD_ANNUAL_HOURS} standard hours).`
                      : `Job cost per hour is derived as salary ÷ ${STANDARD_ANNUAL_HOURS} standard hours.`}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Hourly Rate ($)
                  </label>
                  <input
                    type="number"
                    name="hourly_rate"
                    min="0"
                    step="0.01"
                    value={formData.hourly_rate || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>
              )}

              {/* Salaried staff have no overtime COST in this model: their cost per hour is
                  already the whole-year figure spread over standard hours. */}
              {(formData.pay_type || 'hourly') !== 'salary' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Overtime Rate ($)
                  </label>
                  <input
                    type="number"
                    name="overtime_rate"
                    min="0"
                    step="0.01"
                    value={formData.overtime_rate || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Service, Installation, Office"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Capacity (Hours/Day)
                </label>
                <input
                  type="number"
                  name="capacity_hours_per_day"
                  min="1"
                  max="24"
                  step="0.5"
                  value={formData.capacity_hours_per_day || '8'}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="8"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  name="job_title"
                  value={formData.job_title || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Technician, Project Manager"
                />
              </div>
            </div>
          </div>

          {/* Bottom Half - Module Access */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10">
            <h4 className="text-lg font-semibold text-gray-200 mb-6 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Module Access
            </h4>

            {/* ✅ NEW: Organized by category */}
            <div className="space-y-6">
              {Object.entries({
                Core: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Core'),
                Sales: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Sales'),
                HR: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'HR'),
                Finance: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Finance'),
                Operations: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Operations'),
                Admin: Object.values(EMPLOYEE_MODULES).filter(m => m.category === 'Admin')
              }).map(([category, modules]) => (
                <div key={category}>
                  <h5 className="text-sm font-semibold text-gray-400 mb-3">{category}</h5>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {modules.map(module => {
                      const seatLocked = isSeatLocked(module.key);
                      return (
                      <div key={module.key} className={`flex items-center gap-3 p-3 bg-white/70 dark:bg-slate-800/60 rounded-lg border border-white/20 dark:border-slate-700/40${seatLocked ? ' opacity-70' : ''}`}>
                        <label className={`relative inline-flex items-center ${seatLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={!seatLocked && (permissions[module.key] || false)}
                            onChange={() => handlePermissionToggle(module.key)}
                            disabled={seatLocked}
                            className="sr-only peer"
                          />
                          <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-200">{module.label}</div>
                          <div className="text-xs text-gray-500">
                            {seatLocked ? 'Needs a Full Access seat — change the role to grant this' : module.description}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="border-t border-white/10 bg-white/5 backdrop-blur-sm px-6 py-4 flex justify-between items-center rounded-b-lg">
          {submitError ? (
            <div role="alert" className="text-sm text-red-600 dark:text-red-400">
              {submitError}
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              📧 Employee will receive an email invite to set their password
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Sending Invite...' : 'Send Invite'}
            </button>
          </div>
        </div>
      </form>
    </GlassModal>
  );
};

export default EmployeeInviteModal;
